import React, { useCallback, useEffect, useState } from 'react';
import getStockImportRuns from '../../api/cars/getStockImportRuns';
import getStockImportRun from '../../api/cars/getStockImportRun';
import getStockImportByVin from '../../api/cars/getStockImportByVin';
import Button from '../../components/Button/Button';
import formatDate from '../../utils/formatDate';
import fixDateTimezone from '../../utils/fixDateTimezone';
import styles from './stockImportHistory.module.css';

const when = (value) => (value ? formatDate(fixDateTimezone(value), true) : '');

const sourceTitle = (run) => (run?.source === 'bitrix' ? 'Битрикс' : 'Файлы');

/**
 * История импортов стока.
 *
 * Появилась после разбора расхождения с выгрузкой: машины не было на витрине, а
 * объяснить почему оказалось нечем — результат прогона жил только в памяти, пока
 * открыта вкладка. Теперь каждый прогон сохраняется, и на вопрос «где машина VIN X»
 * отвечает поиск по VIN, а не раскопки в логах.
 */
const StockImportHistory = () => {
  const [runs, setRuns] = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vin, setVin] = useState('');
  const [vinRows, setVinRows] = useState(null);
  const [vinLoading, setVinLoading] = useState(false);

  const loadRuns = useCallback(() => {
    setLoading(true);
    getStockImportRuns({ limit: 10 })
      .then((res) => setRuns(res?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const toggleRun = (id) => {
    if (openRun?.run?.id === id) {
      setOpenRun(null);
      return;
    }
    getStockImportRun(id)
      .then((res) => setOpenRun(res?.data || null))
      .catch(() => {});
  };

  const searchVin = () => {
    const value = vin.trim();
    if (!value) return;
    setVinLoading(true);
    getStockImportByVin(value)
      .then((res) => setVinRows(res?.data || []))
      .catch(() => setVinRows([]))
      .finally(() => setVinLoading(false));
  };

  return (
    <div className={styles.wrapper}>
      <h3 className={styles.title}>История импортов</h3>

      <div className={styles.search}>
        <input
          className={styles.input}
          value={vin}
          placeholder="VIN — почему машина не заехала"
          onChange={(event) => setVin(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && searchVin()}
        />
        <Button text="Проверить" onClick={searchVin} fill disabled={vinLoading} />
      </div>

      {vinRows !== null && (
        <div className={styles.vinResult}>
          {vinRows.length === 0 ? (
            // Пустой ответ — тоже ответ: значит строка доходила до импортёра без
            // ошибок, и причину надо искать не в импорте, а в заказах или в выгрузке.
            <p className={styles.muted}>
              По этому VIN отказов импорта нет. Либо машина заехала, либо её не было в выгрузке.
            </p>
          ) : (
            vinRows.map(({ row, run }) => (
              <p key={row.id} className={styles.vinRow}>
                <b>{when(run?.createdAt)}</b> · {sourceTitle(run)} · {row.dcName || row.cityName || '—'} —{' '}
                {row.reason}
              </p>
            ))
          )}
        </div>
      )}

      {loading && <p className={styles.muted}>Загружаем…</p>}

      {!loading && runs.length === 0 && (
        <p className={styles.muted}>Импортов ещё не было — либо они прошли до появления отчёта.</p>
      )}

      {runs.map((run) => (
        <div key={run.id} className={styles.run}>
          <div className={styles.runHead} onClick={() => toggleRun(run.id)}>
            <span className={styles.runWhen}>{when(run.createdAt)}</span>
            <span>{sourceTitle(run)}: {run.label}</span>
            <span className={styles.runStats}>
              строк {run.totalRows} · заехало {run.imported} ·{' '}
              <b className={run.rejected ? styles.bad : undefined}>отклонено {run.rejected}</b>
            </span>
          </div>

          {run.warnings && <p className={styles.warning}>{run.warnings}</p>}

          {openRun?.run?.id === run.id && (
            <div className={styles.rows}>
              {openRun.rows.length === 0 ? (
                <p className={styles.muted}>Все строки заехали.</p>
              ) : (
                openRun.rows.map((row) => (
                  <p key={row.id} className={styles.row}>
                    <span className={styles.rowVin}>{row.vin || '— без VIN —'}</span>
                    <span>
                      {[row.markName, row.modelName, row.complectationName].filter(Boolean).join(' ')}
                      {row.dcName ? `, ${row.dcName}` : ''}
                    </span>
                    <span className={styles.rowReason}>{row.reason}</span>
                  </p>
                ))
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default StockImportHistory;
